import { breakpoints, Button, Card, Dialog, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants, duration as durationUtil } from '@eti/utils';
import { LoadingDots } from '@eti/components';
import { useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { CancellationRefundOfferReason } from '@eti/schema-types';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { bulletStyles } from '../../../../../utils/styleUtils';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import FormCheckbox from '../../../../../common/components/formComponents/formCheckbox/FormCheckbox';
import { isRequired } from '../../../../../common/components/formComponents/validations';
import type { RefundOfferExtendedType } from './models';
import { CANCEL_WITH_REFUND_OFFER } from './mutations/mutations';
import { getExpirationFormat } from './utils/utils';
import PriceBreakdown from './PriceBreakdown';

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

const rowStyles = css`
  justify-content: space-between;
`;

const descriptionStyle = css`
  padding: 4px 0;
`;

interface RefundOfferDrawerProps {
  description: string;
  refundOffer: RefundOfferExtendedType;
  title: string;
}

const RefundOfferDrawer = ({ description, refundOffer, title }: RefundOfferDrawerProps) => {
  const { t } = useTranslation();
  const { language } = useSiteContext();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { cancellationOptionsMutation, totalPriceIncludingAddOnOrders, paymentTypes } =
    useOrderDetails();
  const {
    unicodeControlCharacters: { LRE, PDF },
  } = constants;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const timeUntilExpiration = useMemo(() => {
    const expiration = getExpirationFormat(refundOffer.expirationTimeInSec, true);
    return durationUtil.getDuration(
      refundOffer.expirationTimeInSec * 1000,
      language.code,
      undefined,
      expiration.units,
      true,
    );
  }, [language.code, refundOffer.expirationTimeInSec]);

  const [
    cancelWithRefundOffer,
    { data: cancellationResponse, error: cancellationError, loading: isLoading },
  ] = useMutation(CANCEL_WITH_REFUND_OFFER, {
    onCompleted: () => {
      setIsDialogOpen(true);

      if (isSnowplowTrackingEnabled) {
        sendTrackEvent({
          category: 'Manage Trip',
          action: 'Pop up view',
          label: 'CANCELLATION_CONFIRMED',
        });
      }
    },
    onError: (error) => {
      if (error.graphQLErrors[0].message !== 'UNAUTHORIZED') {
        setIsDialogOpen(true);

        if (isSnowplowTrackingEnabled) {
          sendTrackEvent({
            category: 'Manage Trip',
            action: 'Pop up view',
            label: error.graphQLErrors[0].message,
          });
        }
      }
    },
  });

  const methods = useForm<{ refundOfferConsent: boolean }>({
    defaultValues: { refundOfferConsent: false },
  });

  const shouldRedirectOnDialogDismiss =
    cancellationResponse?.cancelOrderWithRefundOffer?.success ||
    cancellationError?.graphQLErrors?.[0]?.message === 'ORDER_ALREADY_CANCELLED' ||
    refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund;

  const { handleSubmit } = methods;

  const handleSubmitCancellation = () => {
    if (refundOffer.refundOfferId) {
      cancelWithRefundOffer({
        variables: {
          refundOfferId: refundOffer.refundOfferId,
        },
      });
    }

    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Manage Trip',
        action: 'Click',
        label: 'CANCEL_MY_BOOKING',
      });
    }
  };

  const dialogContent = useMemo(() => {
    if (cancellationResponse?.cancelOrderWithRefundOffer?.success) {
      if (refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund) {
        return {
          buttonText: t('ManageTrip.Cancel.Success.Button.Text'),
          description: null,
          title: t('ManageTrip.RefundRequest.Success.Title'),
        };
      }
      return {
        buttonText: t('ManageTrip.Cancel.Success.Button.Text'),
        description: t('ManageTrip.Cancel.Success.Description'),
        title: t('ManageTrip.Cancel.Success.Title'),
      };
    }

    if (cancellationError?.graphQLErrors?.[0]?.message === 'CANCELLATION_PROCESS_FAILED') {
      return {
        buttonText: t('OrderDetails.CloseButton.Label'),
        description: t('Void.Error.Generic.Description'),
        title: t(
          refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund
            ? 'ManageTrip.RefundRequest.Error.Title'
            : 'ManageTrip.Cancel.Error.Title',
        ),
      };
    }
    if (cancellationError?.graphQLErrors?.[0]?.message === 'ORDER_ALREADY_CANCELLED') {
      if (refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund) {
        return {
          buttonText: t('ManageTrip.Cancel.Success.Button.Text'),
          description: t('ManageTrip.RefundRequest.AlreadySubmitted.Description'),
          title: t('ManageTrip.RefundRequest.Error.Title'),
        };
      }
      return {
        buttonText: t('ManageTrip.Cancel.Success.Button.Text'),
        description: t('Void.Error.OrderCancelled.Description'),
        title: t('ManageTrip.Cancel.Error.Title'),
      };
    }
    if (cancellationError?.graphQLErrors?.[0]?.message === 'TIME_EXPIRED') {
      if (refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund) {
        return {
          buttonText: t('Close'),
          description: t('ManageTrip.RefundRequest.Expired.Description'),
          title: t('ManageTrip.RefundRequest.Expired.Title'),
        };
      }
      const timeExpiredDescription = {
        [CancellationRefundOfferReason.CancelForAnyReason]: t(
          'ManageTrip.CancelForAnyReason.Expired.Dialog.Description',
        ),
        [CancellationRefundOfferReason.TicketRules]: t(
          'ManageTrip.Void.Expired.Dialog.Description',
        ),
        [CancellationRefundOfferReason.Void]: t('ManageTrip.Void.Expired.Dialog.Description'),
      };
      return {
        buttonText: t('ManageTrip.Cancel.Expired.Dialog.Button.Text'),
        description: timeExpiredDescription[refundOffer.refundOfferReason],
        title: t('ManageTrip.Cancel.Error.Title'),
      };
    }
    return {
      buttonText: t('OrderDetails.CloseButton.Label'),
      description: t('Void.Error.Generic.Description'),
      title: t('ManageTrip.Cancel.Error.Title'),
    };
  }, [
    cancellationError?.graphQLErrors,
    cancellationResponse?.cancelOrderWithRefundOffer?.success,
    refundOffer.refundOfferReason,
    t,
  ]);

  const handleDialogDismiss = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Manage Trip',
        action: 'Click',
        label: 'ERROR_DIALOG_BUTTON',
        property: cancellationError?.graphQLErrors?.[0]?.message,
      });
    }
    setIsDialogOpen(false);
    if (cancellationError?.graphQLErrors?.[0]?.message === 'TIME_EXPIRED') {
      cancellationOptionsMutation();
    }
  };

  return (
    <>
      <Card>
        <Stack spacing={24}>
          <Stack>
            <Heading level={3}>{title}</Heading>
            {refundOffer.refundOfferReason !==
              CancellationRefundOfferReason.ScheduleChangeRefund && (
              <Text asHtml data-testid="refund-offer-time-until-expiration">
                {t(
                  'ManageTrip.Cancel.Drawer.Expires.Description',
                  `${LRE}${timeUntilExpiration}${PDF}`,
                )}
              </Text>
            )}
            <Text asHtml className={bulletStyles(0)}>
              {description}
            </Text>
          </Stack>
          {refundOffer.refundOfferReason !== CancellationRefundOfferReason.ScheduleChangeRefund &&
            totalPriceIncludingAddOnOrders !== undefined &&
            paymentTypes !== undefined && (
              <>
                <div className={dividerStyles} />
                <PriceBreakdown refundOffer={refundOffer} />
              </>
            )}
          <FormProvider {...methods}>
            <form noValidate onSubmit={handleSubmit(handleSubmitCancellation)}>
              <Inline alignY="center" className={rowStyles}>
                <FormCheckbox
                  data-testid="refund-offer-consent-checkbox"
                  isRequired
                  label={t('Void.Submit.Checkbox.Label')}
                  name="refundOfferConsent"
                  validations={isRequired(t('Validation.Required'))}
                />
                <Button data-testid="refund-offer-cancel-button" type="submit" variant="primary">
                  {isLoading ? (
                    <LoadingDots />
                  ) : (
                    t(
                      refundOffer.refundOfferReason !==
                        CancellationRefundOfferReason.ScheduleChangeRefund
                        ? 'Void.Submit.Button.Label'
                        : 'ManageTrip.RefundRequest.Title',
                    )
                  )}
                </Button>
              </Inline>
            </form>
          </FormProvider>
        </Stack>
      </Card>
      <Dialog
        closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
        data-testid="successful-cancellation-dialog"
        footer={
          <Inline align="end" collapseBelow={breakpoints._560}>
            {shouldRedirectOnDialogDismiss ? (
              <Button
                as={Link}
                onClick={handleDialogDismiss}
                to={`${routes.ORDER_DETAILS}?show-refund-status=true`}
                variant="primary"
              >
                {dialogContent.buttonText}
              </Button>
            ) : (
              <Button onClick={handleDialogDismiss} variant="primary">
                {dialogContent.buttonText}
              </Button>
            )}
          </Inline>
        }
        illustration={
          cancellationResponse?.cancelOrderWithRefundOffer?.success ? (
            <CheckCircle
              alt={t('ScreenReader.SuccessIcon.AriaLabel.Text')}
              color={vars.colors.signal.positive.light}
              size={64}
              weight="fill"
            />
          ) : (
            <XCircle
              alt={t('ScreenReader.FailureIcon.AriaLabel.Text')}
              color={vars.colors.signal.critical.light}
              size={64}
              weight="fill"
            />
          )
        }
        isOpen={isDialogOpen}
        title={dialogContent.title}
      >
        <Stack spacing={8}>
          <Text asHtml className={descriptionStyle}>
            {dialogContent.description}
          </Text>
        </Stack>
      </Dialog>
    </>
  );
};

export default RefundOfferDrawer;
